import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import ElementTile from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/ElementTile';
import type { ElementTileKind } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/ElementTile';
import { css } from '@emotion/react';
import type * as React from 'react';

const lineStyle = css`
	display: flex;
	align-items: center;
	gap: 0.875rem;
	flex-wrap: wrap;
	padding: 0.75rem 0;
	border-top: 1px solid ${theme.colors.border};
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.1875rem;
	flex: 1;
	min-width: 10rem;
`;

const titleStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.90625rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.35;
	color: ${theme.colors.ink};
`;

const subtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const fullWidthStyle = css`
	flex-basis: 100%;
`;

type Props = {
	readonly kind: ElementTileKind;
	readonly title: React.ReactNode;
	readonly subtitle: string;
	/** Under the text: a link to the detail, « Choisir un autre … ». */
	readonly links?: React.ReactNode;
	/** Secondary text under the subtitle: why the element is in its state. */
	readonly stateReason?: string | null;
	/** State chip. */
	readonly chip: React.ReactNode;
	/** Trailing action (« Retirer »). */
	readonly action?: React.ReactNode;
	/** Full-width row under the line (retry / abandon of a transfer). */
	readonly footer?: React.ReactNode;
};

/** One line of the « Éléments » list: kind tile, title and subtitle, state chip, action. */
const ElementLine: React.FC<Props> = ({
	kind,
	title,
	subtitle,
	links,
	stateReason,
	chip,
	action,
	footer,
}) => {
	return (
		<li css={lineStyle}>
			<ElementTile kind={kind} />
			<div css={textStyle}>
				<div css={titleStyle}>{title}</div>
				<p css={subtitleStyle}>{subtitle}</p>
				<ConditionalLoader data={stateReason}>
					{(reason) => <p css={subtitleStyle}>{reason}</p>}
				</ConditionalLoader>
				{links}
			</div>
			{chip}
			{action}
			<ConditionalLoader data={footer}>
				{(content) => <div css={fullWidthStyle}>{content}</div>}
			</ConditionalLoader>
		</li>
	);
};

export default ElementLine;
